import type { CSSProperties, FC } from 'react'

import { useId, useState } from 'react'
import { Leaf } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, formatUsd } from '@/lib/format'
import { useAnimatedNumber } from '@/hooks/use-animated-number'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/landing/section-heading'
import { savingsAssumptions } from '@/constants/content'

const dayOptions = [250, 300, 360]

interface SliderProps {
    label: string
    value: number
    min: number
    max: number
    unit: string
    onChange: (value: number) => void
}

const Slider: FC<SliderProps> = ({ label, value, min, max, unit, onChange }) => {
    const id = useId()
    const fill = ((value - min) / (max - min)) * 100

    return (
        <div>
            <div className='flex items-baseline justify-between gap-4'>
                <label htmlFor={id} className='text-sm font-medium text-white/80'>
                    {label}
                </label>
                <output htmlFor={id} className='text-sm font-semibold text-white'>
                    {value} <span className='font-normal text-white/55'>{unit}</span>
                </output>
            </div>
            <input
                id={id}
                type='range'
                min={min}
                max={max}
                step={1}
                value={value}
                onChange={event => onChange(Number(event.target.value))}
                className='range-input mt-3'
                style={{ '--fill': `${fill}%` } as CSSProperties}
            />
        </div>
    )
}

export const Savings: FC = () => {
    const [trucks, setTrucks] = useState(6)
    const [hours, setHours] = useState(10)
    const [days, setDays] = useState(250)

    const fleetHours = trucks * hours * days
    const dieselPerHour = savingsAssumptions.dieselCostPerHour + savingsAssumptions.dieselMaintenancePerHour
    const electricPerHour = savingsAssumptions.electricCostPerHour + savingsAssumptions.electricMaintenancePerHour
    const dieselCost = fleetHours * dieselPerHour
    const electricCost = fleetHours * electricPerHour
    const savings = dieselCost - electricCost
    const co2Tonnes = (fleetHours * savingsAssumptions.co2KgPerHour) / 1000

    const animatedSavings = useAnimatedNumber(savings)
    const animatedFiveYear = useAnimatedNumber(savings * 5)
    const animatedCo2 = useAnimatedNumber(co2Tonnes)

    const bars = [
        {
            label: "Diesel fleet",
            cost: dieselCost,
            energy: fleetHours * savingsAssumptions.dieselCostPerHour,
            maintenance: fleetHours * savingsAssumptions.dieselMaintenancePerHour,
            energyLabel: "Fuel",
            accent: false
        },
        {
            label: "FORQ fleet",
            cost: electricCost,
            energy: fleetHours * savingsAssumptions.electricCostPerHour,
            maintenance: fleetHours * savingsAssumptions.electricMaintenancePerHour,
            energyLabel: "Electricity",
            accent: true
        }
    ]

    return (
        <section id='savings' aria-labelledby='savings-title' className='relative z-10 scroll-mt-16 border-t border-white/[0.07] bg-surface py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:items-center lg:gap-20'>
                <div>
                    <SectionHeading
                        id='savings-title'
                        eyebrow="Savings calculator"
                        title="Switch to electric and keep the difference."
                        body="See what a FORQ fleet saves against diesel trucks doing the same work. Move the sliders to match your operation."
                    />

                    <Reveal delay={120}>
                        <ul className='mt-10 space-y-3 text-sm text-white/70'>
                            <li className='flex gap-3'>
                                <span aria-hidden='true' className='mt-2 size-1.5 shrink-0 rounded-full bg-brand' />
                                No fuel deliveries, oil changes, filters or exhaust treatment.
                            </li>
                            <li className='flex gap-3'>
                                <span aria-hidden='true' className='mt-2 size-1.5 shrink-0 rounded-full bg-brand' />
                                Charge on cheaper off-peak power and top up during breaks.
                            </li>
                            <li className='flex gap-3'>
                                <span aria-hidden='true' className='mt-2 size-1.5 shrink-0 rounded-full bg-brand' />
                                Zero exhaust indoors, so no ventilation upgrades for enclosed sites.
                            </li>
                        </ul>

                        <p className='mt-10 max-w-xl text-xs leading-relaxed text-white/45'>
                            Estimates assume diesel at $4.00 a gallon and 0.85 gallons an hour, electricity at $0.14 per kWh and 5 kWh an hour, and typical maintenance costs for each truck type. Your results will vary with load, duty cycle and local energy prices.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={80}>
                    <div className='rounded-3xl border border-white/10 bg-card p-6 shadow-[0_40px_120px_-50px_rgba(0,0,0,0.9)] sm:p-8'>
                        <div className='grid gap-7'>
                            <Slider label="Trucks in your fleet" value={trucks} min={1} max={50} unit="trucks" onChange={setTrucks} />
                            <Slider label="Operating hours per day" value={hours} min={2} max={24} unit="h" onChange={setHours} />

                            <fieldset>
                                <legend className='text-sm font-medium text-white/80'>
                                    Operating days per year
                                </legend>
                                <div className='mt-3 grid grid-cols-3 gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1'>
                                    {dayOptions.map(option => (
                                        <label
                                            key={option}
                                            className={cn(
                                                'cursor-pointer rounded-full py-2 text-center text-sm font-medium transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand',
                                                days === option ? 'bg-white text-background' : 'text-white/65 hover:text-white'
                                            )}
                                        >
                                            <input
                                                type='radio'
                                                name='savings-days'
                                                value={option}
                                                checked={days === option}
                                                onChange={() => setDays(option)}
                                                className='sr-only'
                                            />
                                            {option} days
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                        </div>

                        <div className='mt-9 border-t border-white/10 pt-8'>
                            <p className='text-sm text-white/60'>
                                Estimated savings per year
                            </p>
                            <p aria-hidden='true' className='mt-1 text-5xl font-semibold tracking-tight text-white sm:text-6xl'>
                                {formatUsd(animatedSavings)}
                            </p>
                            <p className='sr-only' aria-live='polite'>
                                {formatUsd(savings)} saved per year
                            </p>

                            <dl className='mt-6 grid grid-cols-2 gap-3'>
                                <div className='flex flex-col-reverse gap-1 rounded-2xl border border-white/10 bg-white/[0.02] p-4'>
                                    <dt className='text-xs text-white/55'>Saved over 5 years</dt>
                                    <dd className='text-xl font-semibold text-white'>{formatUsd(animatedFiveYear)}</dd>
                                </div>
                                <div className='flex flex-col-reverse gap-1 rounded-2xl border border-white/10 bg-white/[0.02] p-4'>
                                    <dt className='flex items-center gap-1.5 text-xs text-white/55'>
                                        <Leaf aria-hidden='true' className='size-3.5 text-brand' />
                                        CO₂ avoided each year
                                    </dt>
                                    <dd className='text-xl font-semibold text-white'>
                                        {formatNumber(animatedCo2)}
                                        <span className='ml-1 text-sm font-normal text-white/55'>tonnes</span>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div className='mt-8'>
                            <p className='text-sm font-medium text-white/80'>
                                Running cost per year
                            </p>
                            <ul className='mt-4 space-y-4'>
                                {bars.map(bar => {
                                    const width = (bar.cost / Math.max(dieselCost, 1)) * 78

                                    return (
                                        <li
                                            key={bar.label}
                                            tabIndex={0}
                                            className='group relative grid grid-cols-[6.5rem_1fr] items-center gap-3 rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand sm:grid-cols-[7.5rem_1fr]'
                                        >
                                            <span className='text-sm text-white/70'>
                                                {bar.label}
                                            </span>
                                            <span className='flex items-center gap-3'>
                                                <span
                                                    className={cn(
                                                        'h-5 rounded-r-[4px] transition-[width,filter] duration-500 ease-out group-hover:brightness-125 group-focus-visible:brightness-125',
                                                        bar.accent ? 'bg-brand' : 'bg-white/25'
                                                    )}
                                                    style={{ width: `${Math.max(width, 1.5)}%` }}
                                                />
                                                <span className='shrink-0 text-sm font-semibold text-white'>
                                                    {formatUsd(bar.cost)}
                                                </span>
                                            </span>

                                            <span
                                                role='tooltip'
                                                className='pointer-events-none absolute -top-14 left-[6.5rem] z-10 rounded-lg border border-white/10 bg-popover px-3 py-2 text-xs opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 sm:left-[7.5rem]'
                                            >
                                                <span className='block whitespace-nowrap'>
                                                    <span className='font-semibold text-white'>{formatUsd(bar.energy)}</span>
                                                    <span className='text-white/55'> {bar.energyLabel}</span>
                                                </span>
                                                <span className='block whitespace-nowrap'>
                                                    <span className='font-semibold text-white'>{formatUsd(bar.maintenance)}</span>
                                                    <span className='text-white/55'> Maintenance</span>
                                                </span>
                                            </span>
                                        </li>
                                    )
                                })}
                            </ul>

                            <table className='sr-only'>
                                <caption>Running cost per year by fleet type</caption>
                                <thead>
                                    <tr>
                                        <th scope='col'>Fleet</th>
                                        <th scope='col'>Energy</th>
                                        <th scope='col'>Maintenance</th>
                                        <th scope='col'>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bars.map(bar => (
                                        <tr key={bar.label}>
                                            <th scope='row'>{bar.label}</th>
                                            <td>{formatUsd(bar.energy)}</td>
                                            <td>{formatUsd(bar.maintenance)}</td>
                                            <td>{formatUsd(bar.cost)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
